"use client"

import { useState, useEffect, useContext } from "react"
import { CheckCircle, Loader2 } from "lucide-react"
import axios from "axios"
import { AuthContext } from "../../../Auth/context/AuthContext"
import type { ImportRow } from "../../types"

interface ProgressStepProps {
  importedRecords: ImportRow[]
  jobId: string | null
  onComplete: () => void
  onError: (error: string) => void
}

export default function ProgressStep({ importedRecords, jobId, onComplete }: ProgressStepProps) {
  const [progress, setProgress] = useState(0)
  const [status, setStatus] = useState<"processing" | "complete" | "error">("processing")
  const [message, setMessage] = useState("Processing your import...")
  const { auth } = useContext(AuthContext)

  useEffect(() => {
    // auth is obtained outside useEffect
    if (status !== "processing" || !jobId || !auth?.token) return

    const poll = setInterval(async () => {
      try {
        const resp = await axios.get(
          `/api/myteconstruction/contact-import/jobs/${jobId}`,
          { headers: { Authorization: `Bearer ${auth.token}` } }
        )
        const job = resp.data.data
        const pct = Math.round((job.processedRows / job.totalRows) * 100)
        setProgress(Math.min(pct, 100))

        if (job.status === "done") {
          clearInterval(poll)
          setStatus("complete")
          setMessage("Import completed successfully!")
        } else if (job.status === "error") {
          clearInterval(poll)
          setStatus("error")
          setMessage(job.errorMessage || "Import failed.")
        }
      } catch (err) {
        console.error("Job polling error:", err)
      }
    }, 2000)

    return () => clearInterval(poll)
  }, [status, jobId, auth?.token])

  return (
    <div>
      <h3 className="text-lg font-medium text-white mb-4">Import Progress</h3>

      <div className="space-y-6">
        {status === "processing" ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 text-blue-500 animate-spin mb-4" />
            <p className="text-lg font-medium text-white mb-2">{message}</p>
            <div className="w-full max-w-md bg-gray-700 rounded-full h-4 mb-2">
              <div
                className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <p className="text-sm text-gray-400">{progress}% complete</p>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-8">
            <CheckCircle className="h-16 w-16 text-green-500 mb-4" />
            <p className="text-lg font-medium text-white mb-2">{message}</p>
            <p className="text-sm text-gray-400 mb-6">Successfully imported {importedRecords.length} contacts</p>
            <button
              onClick={onComplete}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
            >
              View Contacts
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
